import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import '../css/ProjectForm.css';
import { NewProjectDialogForm } from '../jsx/newProjectDialogForm';
import { parseApiErrorResponse, validateUploadFile } from '../utils/uploadLimits';

const endpointpath = process.env.REACT_APP_API_BASE_URL;

const CHARACTER_LIMITS = Object.freeze({
  requesterDepartment: 120,
  requestTitle: 200,
  projectSummary: 1200,
  desiredOutcomes: 600,
  collaborators: 500,
  locationDetails: 500,
  projectRestrictions: 1200,
  availableResources: 600,
  fundingDetails: 600,
  specialInstructions: 600,
});

const KEYWORD_LIMIT = 5;

const PUBLISH_FIELD_LABELS = Object.freeze({
  requesterTitle: 'Title',
  requesterDepartment: 'Department',
  requestTitle: 'Project Title',
  keywords: 'Keywords',
  projectSummary: 'Project Summary',
  desiredOutcomes: 'Desired Outcomes',
  proposalDeadline: 'Proposal Deadline',
  startDate: 'Estimated Start Date',
  endDate: 'Estimated End Date',
  collaborators: 'Collaborators',
  locationDetails: 'Location Details',
  projectRestrictions: 'Project Restrictions',
  availableResources: 'Available Resources',
  fundingDetails: 'Funding Details',
  specialInstructions: 'Special Instructions',
});

const computePublishErrors = (data) => {
  const newErrors = {};
  const today = new Date().toISOString().split('T')[0];
  const twoMonthsLater = new Date();
  twoMonthsLater.setMonth(twoMonthsLater.getMonth() + 2);
  const twoMonthsIso = twoMonthsLater.toISOString().split('T')[0];

  if (!data.requesterTitle) newErrors.requesterTitle = 'Title is required';
  if (!data.requesterDepartment) newErrors.requesterDepartment = 'Department is required';
  if (!data.requestTitle || data.requestTitle.length > CHARACTER_LIMITS.requestTitle) {
    newErrors.requestTitle = `Max ${CHARACTER_LIMITS.requestTitle} characters`;
  }
  if (!data.keywords || data.keywords.length === 0) {
    newErrors.keywords = 'Please add at least one keyword.';
  } else if (data.keywords.length > KEYWORD_LIMIT) {
    newErrors.keywords = `Limit ${KEYWORD_LIMIT} keywords.`;
  }
  if (!data.projectSummary || data.projectSummary.length > CHARACTER_LIMITS.projectSummary) {
    newErrors.projectSummary = `Max ${CHARACTER_LIMITS.projectSummary} characters`;
  }
  if (!data.desiredOutcomes || data.desiredOutcomes.length > CHARACTER_LIMITS.desiredOutcomes) {
    newErrors.desiredOutcomes = `Max ${CHARACTER_LIMITS.desiredOutcomes} characters`;
  }
  if (!data.proposalDeadline || data.proposalDeadline < today || data.proposalDeadline > twoMonthsIso) {
    newErrors.proposalDeadline = 'Must be within 2 months from today';
  }
  if (!data.startDate || data.startDate < today || data.startDate < data.proposalDeadline) {
    newErrors.startDate = 'Must be today or later, and on or after the Proposal Deadline';
  }
  if (!data.endDate || data.endDate < data.startDate) {
    newErrors.endDate = 'Must be on or after the Estimated Start Date';
  }
  if (data.collaborators && data.collaborators.length > CHARACTER_LIMITS.collaborators) {
    newErrors.collaborators = `Max ${CHARACTER_LIMITS.collaborators} characters`;
  }
  if (
    data.specificLocation === 'YES' &&
    data.locationDetails &&
    data.locationDetails.length > CHARACTER_LIMITS.locationDetails
  ) {
    newErrors.locationDetails = `Max ${CHARACTER_LIMITS.locationDetails} characters`;
  }
  if (data.projectRestrictions && data.projectRestrictions.length > CHARACTER_LIMITS.projectRestrictions) {
    newErrors.projectRestrictions = `Max ${CHARACTER_LIMITS.projectRestrictions} characters`;
  }
  if (data.availableResources && data.availableResources.length > CHARACTER_LIMITS.availableResources) {
    newErrors.availableResources = `Max ${CHARACTER_LIMITS.availableResources} characters`;
  }
  if (data.studentFunding !== 'No Funding Available' && !data.fundingDetails) {
    newErrors.fundingDetails = 'Funding details required when funding is provided';
  } else if (data.fundingDetails && data.fundingDetails.length > CHARACTER_LIMITS.fundingDetails) {
    newErrors.fundingDetails = `Max ${CHARACTER_LIMITS.fundingDetails} characters`;
  }
  if (data.specialInstructions && data.specialInstructions.length > CHARACTER_LIMITS.specialInstructions) {
    newErrors.specialInstructions = `Max ${CHARACTER_LIMITS.specialInstructions} characters`;
  }

  return newErrors;
};

const formatPublishErrorSummary = (publishErrors) => {
  const entries = Object.entries(publishErrors);
  if (entries.length === 0) return '';

  const details = entries
    .map(([field, message]) => `${PUBLISH_FIELD_LABELS[field] || field}: ${message}`)
    .join(' ');

  return `Please fix the following before publishing. ${details}`;
};
const normalizeKeywords = (value) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(/[;,]/)
      .map((keyword) => keyword.trim())
      .filter(Boolean);
  }
  return [];
};

const normalizeFileField = (value) => {
  if (!value) return '';
  if (typeof value !== 'string') return value;
  const normalized = value.trim();
  const lower = normalized.toLowerCase();
  if (lower.startsWith('no ') || lower.includes('not available')) {
    return '';
  }
  return normalized;
};

const generateProjectId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `proj_${Date.now()}_${Math.random().toString(16).slice(2, 10)}`;
};

// --- Begin SSO/JWT identity helpers ---
const decodeJwt = (token) => {
  try {
    const payload = token.split('.')[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(base64);
    return JSON.parse(
      decodeURIComponent(
        json
          .split('')
          .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
          .join('')
      )
    );
  } catch (e) {
    return null;
  }
};

const getUserFromToken = () => {
  const token = localStorage.getItem('token');
  if (!token) return null;
  const decoded = decodeJwt(token);
  if (!decoded) return null;
  return {
    FirstName:
      decoded.FirstName ||
      decoded.firstName ||
      decoded.first_name ||
      decoded.given_name ||
      decoded.givenName ||
      '',
    LastName:
      decoded.LastName ||
      decoded.lastName ||
      decoded.last_name ||
      decoded.family_name ||
      decoded.surname ||
      '',
    DisplayName: decoded.DisplayName || decoded.displayName || decoded.name || decoded.cn || '',
    Email:
      decoded.Email ||
      decoded.email ||
      decoded.mail ||
      decoded.userPrincipalName ||
      decoded.upn ||
      decoded.preferred_username ||
      '',
    NetID: decoded.NetID || decoded.netid || '',
    Department: decoded.Department || decoded.department || '',
    Honorific: decoded.Honorific || decoded.title || '',
  };
};

const resolveEmail = (user) => {
  if (!user) return '';
  return (
    user.Email ||
    user.email ||
    user.mail ||
    user.userPrincipalName ||
    user.upn ||
    user.preferred_username ||
    (user.NetID ? `${user.NetID}@tamu.edu` : '')
  );
};

const resolveName = (user) => {
  if (!user) return '';
  const first =
    user.FirstName || user.firstName || user.first_name || user.given_name || user.givenName || '';
  const last =
    user.LastName || user.lastName || user.last_name || user.family_name || user.surname || '';
  const display = user.DisplayName || user.displayName || user.name || user.cn || '';
  const combined = `${first} ${last}`.trim();
  if (combined) return combined;
  if (display) {
    if (display.includes(',')) {
      const parts = display
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean);
      if (parts.length === 2) {
        return `${parts[1]} ${parts[0]}`.trim();
      }
    }
    return display.trim();
  }
  const email = resolveEmail(user);
  if (!email) return '';
  return email
    .split('@')[0]
    .replace(/[._-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
};
// --- End SSO/JWT identity helpers ---

export const ProjectRequestForm = ({ userData }) => {
  const effectiveUser = userData || getUserFromToken();

  const resolvedUserType =
    (userData && userData.userType) ||
    localStorage.getItem('userType') ||
    (effectiveUser && (effectiveUser.userType || effectiveUser.UserType)) ||
    '';

  const normalizeBearer = (token) => {
    if (!token) return '';
    return token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  };

  const buildAuthHeaders = (token) => {
    const authValue = normalizeBearer(token);
    return {
      ...(authValue ? { Authorization: authValue } : {}),
      ...(resolvedUserType ? { usertype: resolvedUserType.toLowerCase() } : {}),
    };
  };

  const defaultName = resolveName(effectiveUser);
  const defaultEmail = resolveEmail(effectiveUser);
  const defaultTitle = effectiveUser?.Honorific || '';
  const defaultDepartment = effectiveUser?.Department || '';

  const [formData, setFormData] = useState({
    requesterName: defaultName,
    requesterTitle: defaultTitle,
    requesterDepartment: defaultDepartment,
    requesterEmail: defaultEmail,
    projectID: '',
    projectStatus: '',
    requestTitle: '',
    proposalDeadline: '',
    startDate: '',
    endDate: '',
    keywords: [],
    projectSummary: '',
    desiredOutcomes: '',
    collaborators: '',
    specificLocation: 'NO',
    locationDetails: '',
    projectRestrictions: '',
    availableResources: '',
    studentFunding: 'No Funding Available',
    fundingDetails: '',
    alternateContact: '',
    specialInstructions: '',
    proposalVideo: '',
    supplementalUpload: '',
  });

  const videoInputRef = useRef(null);
  const documentInputRef = useRef(null);

  const [searchParams] = useSearchParams();
  const [errors, setErrors] = useState({});
  const [isPublishDisabled, setIsPublishDisabled] = useState(true);
  const [isSavedDisabled, setIsSaveDisabled] = useState(true);
  const [currentStatus, setCurrentStatus] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isPrefilling, setIsPrefilling] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  const requireToken = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setFeedback({ severity: 'error', message: 'Session expired: please log in again.' });
      return null;
    }
    return token;
  };

  useEffect(() => {
    if (effectiveUser) {
      setFormData((prev) => ({
        ...prev,
        requesterName: prev.requesterName || resolveName(effectiveUser),
        requesterEmail: prev.requesterEmail || resolveEmail(effectiveUser),
        requesterTitle: prev.requesterTitle || effectiveUser.Honorific || prev.requesterTitle,
        requesterDepartment: prev.requesterDepartment || effectiveUser.Department || prev.requesterDepartment,
      }));
    }
  }, [effectiveUser, userData]);

  useEffect(() => {
    if (formData.projectStatus && formData.projectStatus !== currentStatus) {
      setCurrentStatus(formData.projectStatus);
    }
  }, [formData.projectStatus, currentStatus]);

  useEffect(() => {
    const mode = searchParams.get('mode');
    const projectId = searchParams.get('projectID');
    if (mode !== 'edit' || !projectId) return;

    const token = requireToken();
    if (!token) return;

    const headers = buildAuthHeaders(token);

    const fetchProject = async () => {
      setIsPrefilling(true);
      try {
        const response = await fetch(`${endpointpath}/api/projects`, { headers });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.error || data?.message || 'Failed to load project.');
        }
        const projects = data?.projects || [];
        const match = projects.find((entry) => entry.projectID === projectId);
        if (!match) {
          setFeedback({
            severity: 'error',
            message: 'Project not found or you do not have access to it.',
          });
          return;
        }

        setFormData((prev) => ({
          ...prev,
          projectID: match.projectID || prev.projectID || projectId,
          projectStatus: match.projectStatus || prev.projectStatus || '',
          requesterName: match.requesterName || prev.requesterName || defaultName,
          requesterTitle: match.requesterTitle || prev.requesterTitle || defaultTitle,
          requesterDepartment:
            match.requesterDepartment || prev.requesterDepartment || defaultDepartment,
          requesterEmail: match.requesterEmail || prev.requesterEmail || defaultEmail,
          requestTitle: match.requestTitle || '',
          proposalDeadline: match.proposalDeadline || '',
          startDate: match.startDate || '',
          endDate: match.endDate || '',
          keywords: normalizeKeywords(match.keywords),
          projectSummary: match.projectSummary || '',
          desiredOutcomes: match.desiredOutcomes || '',
          collaborators: match.collaborators || '',
          specificLocation: match.specificLocation || 'NO',
          locationDetails: match.locationDetails || '',
          projectRestrictions: match.projectRestrictions || '',
          availableResources: match.availableResources || '',
          studentFunding: match.studentFunding || 'No Funding Available',
          fundingDetails: match.fundingDetails || '',
          alternateContact: match.alternateContact || '',
          specialInstructions: match.specialInstructions || '',
          proposalVideo: normalizeFileField(match.proposalVideo),
          supplementalUpload: normalizeFileField(match.supplementalUpload),
        }));
        setCurrentStatus(match.projectStatus || '');
        setFeedback(null);
      } catch (error) {
        console.error('Failed to prefill project:', error);
        setFeedback({
          severity: 'error',
          message: error.message || 'Failed to load project details.',
        });
      } finally {
        setIsPrefilling(false);
      }
    };

    fetchProject();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, defaultName, defaultTitle, defaultDepartment, defaultEmail]);

  useEffect(() => {
    validateForm();
  }, [formData]);

  const validateForm = () => {
    const newErrors = computePublishErrors(formData);

    const canQuickSave =
      Boolean(formData.requestTitle) &&
      (!CHARACTER_LIMITS.requestTitle || formData.requestTitle.length <= CHARACTER_LIMITS.requestTitle) &&
      Boolean(resolveName(effectiveUser)) &&
      Boolean(resolveEmail(effectiveUser));

    setErrors(newErrors);
    setIsSaveDisabled(!canQuickSave);
    setIsPublishDisabled(Object.keys(newErrors).length > 0);
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleTextChange = (field, value) => {
    const limit = CHARACTER_LIMITS[field];
    updateField(field, limit ? value.slice(0, limit) : value);
  };

  const handleDateChange = (field, value) => {
    if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
      updateField(field, '');
      return;
    }
    updateField(field, value.toISOString().split('T')[0]);
  };

  const handleKeywordAdd = (keyword) => {
    const trimmed = keyword.trim();
    if (!trimmed) return;
    setFormData((prev) => {
      if (prev.keywords.includes(trimmed) || prev.keywords.length >= KEYWORD_LIMIT) {
        return prev;
      }
      return { ...prev, keywords: [...prev.keywords, trimmed] };
    });
  };

  const handleKeywordRemove = (keyword) => {
    setFormData((prev) => ({
      ...prev,
      keywords: prev.keywords.filter((value) => value !== keyword),
    }));
  };

  const handleFileChange = (event) => {
    const { name, files } = event.target;
    const file = files?.[0] || null;
    if (!file) return;

    const validationError = validateUploadFile(name, file, resolvedUserType || 'employer');
    if (validationError) {
      setFeedback({ severity: 'error', message: validationError });
      event.target.value = '';
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: file,
    }));
  };

  const handleDeleteFile = (field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: null,
    }));
    if (field === 'supplementalUpload' && documentInputRef.current) {
      documentInputRef.current.value = '';
    }
    if (field === 'proposalVideo' && videoInputRef.current) {
      videoInputRef.current.value = '';
    }
  };

  const createFormPayload = (rawData) => {
    const fallbackUser = effectiveUser || {};
    const enforced = {
      ...rawData,
      requesterName: rawData.requesterName || resolveName(fallbackUser),
      requesterEmail: rawData.requesterEmail || resolveEmail(fallbackUser),
      requesterTitle: rawData.requesterTitle || fallbackUser.Honorific || rawData.requesterTitle,
      requesterDepartment: rawData.requesterDepartment || fallbackUser.Department || rawData.requesterDepartment,
    };
    const formPayload = new FormData();
    for (const [key, value] of Object.entries(enforced)) {
      let payloadValue = value;
      if (key === 'keywords') {
        payloadValue = Array.isArray(value) ? value.join(';') : value || '';
      }
      if (payloadValue !== undefined && payloadValue !== null) {
        formPayload.append(key, payloadValue);
      }
    }
    return formPayload;
  };

  const ensureProjectIdentity = () => {
    if (formData.projectID) {
      return { projectID: formData.projectID, payload: formData };
    }
    const newId = generateProjectId();
    const next = { ...formData, projectID: newId };
    setFormData(next);
    return { projectID: newId, payload: next };
  };

  const parseResponsePayload = parseApiErrorResponse;

  const handleSave = async (event) => {
    event?.preventDefault?.();
    const token = requireToken();
    if (!token) return;

    const { projectID, payload } = ensureProjectIdentity();
    const formPayload = createFormPayload({ ...payload, projectStatus: 'Saved' });
    const headers = buildAuthHeaders(token);

    setFeedback(null);
    setIsSaving(true);

    try {
      const response = await fetch(`${endpointpath}/api/save`, {
        method: 'POST',
        headers,
        body: formPayload,
      });
      const data = await parseResponsePayload(response);
      if (!response.ok) {
        throw new Error(data?.error || data?.message || 'Failed to save project.');
      }
      setCurrentStatus('Saved');
      setFormData((prev) => ({ ...prev, projectID, projectStatus: 'Saved' }));
      setFeedback({
        severity: 'success',
        message: data?.message || 'Project saved successfully.',
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setFeedback({
        severity: 'error',
        message: error.message || 'Failed to save project.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async (event) => {
    event?.preventDefault?.();
    const token = requireToken();
    if (!token) return;

    const publishErrors = computePublishErrors(formData);
    setErrors(publishErrors);
    if (Object.keys(publishErrors).length > 0) {
      setShowValidationErrors(true);
      setFeedback({
        severity: 'error',
        message: formatPublishErrorSummary(publishErrors),
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setShowValidationErrors(false);
    const { projectID, payload } = ensureProjectIdentity();
    const formPayload = createFormPayload({ ...payload, projectStatus: 'Submitted' });
    const headers = buildAuthHeaders(token);

    setFeedback(null);
    setIsSubmitting(true);

    try {
      const response = await fetch(`${endpointpath}/api/submit`, {
        method: 'POST',
        headers,
        body: formPayload,
      });
      const data = await parseResponsePayload(response);
      if (!response.ok) {
        throw new Error(data?.error || data?.message || 'Failed to publish project.');
      }
      setCurrentStatus('Submitted');
      setFormData((prev) => ({ ...prev, projectID, projectStatus: 'Submitted' }));
      setFeedback({
        severity: 'success',
        message: data?.message || 'Project published successfully.',
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setFeedback({
        severity: 'error',
        message: error.message || 'Failed to publish project.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStatusUpdate = async ({ endpoint, nextStatus, successMessage, pendingSetter }) => {
    const token = requireToken();
    if (!token) return;

    if (!formData.projectID) {
      setFeedback({
        severity: 'error',
        message: 'Project identifier missing. Save the project first.',
      });
      return;
    }

    const headers = {
      ...buildAuthHeaders(token),
      'Content-Type': 'application/json',
    };

    setFeedback(null);
    pendingSetter(true);

    try {
      const response = await fetch(`${endpointpath}/api/${endpoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          projectID: formData.projectID,
          userType: resolvedUserType || 'employer',
        }),
      });
      const data = await parseResponsePayload(response);
      if (!response.ok) {
        throw new Error(data?.error || data?.message || `Failed to update project status to ${nextStatus}.`);
      }
      setCurrentStatus(nextStatus);
      setFormData((prev) => ({ ...prev, projectStatus: nextStatus }));
      setFeedback({
        severity: 'success',
        message: data?.message || successMessage,
      });
    } catch (error) {
      console.error('Status update error:', error);
      setFeedback({
        severity: 'error',
        message: error.message || `Failed to update project status to ${nextStatus}.`,
      });
    } finally {
      pendingSetter(false);
    }
  };

  const handleCancelProject = () =>
    handleStatusUpdate({
      endpoint: 'withdraw',
      nextStatus: 'Withdrawn',
      successMessage: 'Project cancelled successfully.',
      pendingSetter: setIsCancelling,
    });

  const handleCloseProject = () =>
    handleStatusUpdate({
      endpoint: 'close',
      nextStatus: 'Closed',
      successMessage: 'Project closed successfully.',
      pendingSetter: setIsClosing,
    });

  const handleFeedbackClear = () => setFeedback(null);

  return (
    <NewProjectDialogForm
      asPage
      formData={formData}
      errors={errors}
      characterLimits={CHARACTER_LIMITS}
      onFieldChange={handleTextChange}
      onDateChange={handleDateChange}
      onKeywordAdd={handleKeywordAdd}
      onKeywordRemove={handleKeywordRemove}
      onSave={handleSave}
      onSubmit={handleSubmit}
      onCancelProject={handleCancelProject}
      onCloseProject={handleCloseProject}
      onFileChange={handleFileChange}
      onFileRemove={handleDeleteFile}
      videoInputRef={videoInputRef}
      documentInputRef={documentInputRef}
      isPublishDisabled={isPublishDisabled}
      isSavedDisabled={isSavedDisabled}
      isSaving={isSaving}
      isSubmitting={isSubmitting}
      isCancelling={isCancelling}
      isClosing={isClosing}
      isPrefilling={isPrefilling}
      status={currentStatus}
      feedback={feedback}
      onFeedbackClear={handleFeedbackClear}
      showValidationErrors={showValidationErrors}
      userInfo={effectiveUser}
    />
  );
};
